/**
 * Save schema versioning.
 *
 * Every save records the schema version it was written with. On load, migrations
 * run in order from that version to CURRENT_SCHEMA. A migration takes the raw
 * parsed save object and returns the next version's shape — it never sees typed
 * arrays or the live GameState, only the serialised form.
 *
 * DEVELOPMENT POLICY (until Phase 12): migrations are *optional*. Bumping the schema
 * without adding one means older saves are rejected with a clear message. That is
 * the right trade while the rules change weekly; writing migrations for a format
 * nobody has saved a real game in is wasted work. From Phase 12 the policy inverts
 * and every bump requires a migration.
 */

export const CURRENT_SCHEMA = 17;

/** Raw serialised save, before it becomes a GameState. */
export type RawSave = Record<string, unknown>;

type Migration = (save: RawSave) => RawSave;

/**
 * Keyed by the version being migrated *from*. `MIGRATIONS[1]` upgrades a v1 save to v2.
 * Empty at v1 because there is nothing older than the first format.
 */
const MIGRATIONS: Record<number, Migration> = {
  /**
   * v4 → v5: research arrives (Phase 6a).
   *
   * Written despite the development policy above, because Mark has games in progress and
   * the change is purely additive: every civilisation starts knowing nothing, researching
   * nothing, with nothing stored. The science rate is left null on purpose — choosing a
   * sensible one needs the budget, which needs content, which a migration does not have.
   * `deserializeGame` fills it in once the state exists (see there).
   */
  4: (save) => {
    const civs = (save['civs'] as Array<Record<string, unknown>>).map((civ) => ({
      ...civ,
      researchRate: null,
      techs: [],
      researching: -1,
      researchGoal: -1,
      researchStore: 0,
    }));
    const content = { ...(save['content'] as Record<string, unknown>), technologies: [] };
    return { ...save, civs, content, schema: 5 };
  },

  /**
   * v5 → v6: specialists. Every city starts on Auto, and the save gains the specialist
   * key table so a reordered specialists.json remaps rather than swaps jobs.
   */
  5: (save) => {
    const cities = (save['cities'] as Array<Record<string, unknown>>).map((city) => ({ ...city, specialist: -1 }));
    const content = { ...(save['content'] as Record<string, unknown>), specialists: [] };
    return { ...save, cities, content, schema: 6 };
  },
  /**
   * v6 → v7: war and peace (Phase 7a). Everyone was at peace — there was no other
   * state — so no relations are recorded, and nobody has been defeated.
   */
  6: (save) => {
    const civs = (save['civs'] as Array<Record<string, unknown>>).map((civ) => ({ ...civ, defeated: false }));
    return { ...save, civs, relations: [], schema: 7 };
  },
  /**
   * v7 → v8: ships carry units (Phase 7b). Nobody was aboard anything; every unit was
   * already Green, which is what its experience of 0 now means.
   */
  7: (save) => {
    const units = (save['units'] as Array<Record<string, unknown>>).map((unit) => ({ ...unit, aboard: -1 }));
    return { ...save, units, schema: 8 };
  },
  /**
   * v8 → v9: AI personalities (Phase 8). The migration cannot see content, so it marks
   * every civilisation unassigned; `deserializeGame` gives each the personality its
   * civilisation leans to. The save gains the personality key table.
   */
  8: (save) => {
    const civs = (save['civs'] as Array<Record<string, unknown>>).map((civ) => ({ ...civ, personality: -1 }));
    const content = { ...(save['content'] as Record<string, unknown>), personalities: [] };
    return { ...save, civs, content, schema: 9 };
  },
  /**
   * v9 → v10: roads get their own layer (ADR-0055). The tile planes are encoded here,
   * so the move is done by `deserializeGame`, which sees no road plane and lifts roads
   * out of the improvement slot itself.
   */
  9: (save) => ({ ...save, schema: 10 }),
  /**
   * v10 → v11: one palace per civilisation (ADR-0057). Earlier builds let a palace be
   * built anywhere for nothing, and the AI built one in every city. Content is needed
   * to know which building is the palace, so `deserializeGame` removes the extras.
   */
  10: (save) => ({ ...save, schema: 11 }),
  /**
   * v11 → v12: governments and war weariness (Phase 10a, ADR-0059). The migration cannot
   * see content, so every civilisation is marked unassigned (-1); `deserializeGame` puts
   * each under the starting government. The save gains the government key table.
   */
  11: (save) => {
    const turn = ((save['meta'] as Record<string, unknown> | undefined)?.['turn'] as number | undefined) ?? 1;
    const civs = (save['civs'] as Array<Record<string, unknown>>).map((civ) => ({
      ...civ,
      government: -1,
      nextGovernment: -1,
      anarchyEnds: 0,
      governmentSince: turn,
      weariness: 0,
    }));
    const content = { ...(save['content'] as Record<string, unknown>), governments: [] };
    return { ...save, civs, content, schema: 12 };
  },
  /**
   * v12 → v13: diplomacy (Phase 10b, ADR-0060). Every relation gains a treaty, open
   * borders, alliances, memories and offer timing; every civilisation a clean
   * reputation; the game, its lists of payments and pending offers. Peace already made
   * is treated as a treaty that has run out.
   */
  12: (save) => {
    const civs = (save['civs'] as Array<Record<string, unknown>>).map((civ) => ({ ...civ, reputation: 0 }));
    const relations = ((save['relations'] as Array<Record<string, unknown>> | undefined) ?? []).map((r) => ({
      ...r,
      treatyUntil: 0,
      openBorders: false,
      alliances: [],
      memoriesA: [],
      memoriesB: [],
      lastDealA: -1,
      lastDealB: -1,
    }));
    return { ...save, civs, relations, payments: [], offers: [], schema: 13 };
  },
  /** v13 → v14: score and victory (Phase 11, ADR-0061). No result yet, and history starts now. */
  13: (save) => ({ ...save, result: null, history: { turns: [], civs: {} }, schema: 14 }),
  /**
   * v14 → v15: strategic resources (Phase 12b, ADR-0063). No resources are shared yet.
   * The map keeps the deposits it was made with: an older world has no oil.
   */
  14: (save) => ({ ...save, grants: [], schema: 15 }),
  /**
   * v15 → v16: war plans and peace terms (Phase 13c, ADR-0071). A war under way gets a
   * plan the next time its AI side moves; an offer already made carries no terms.
   */
  15: (save) => {
    const relations = ((save['relations'] as Array<Record<string, unknown>> | undefined) ?? []).map((r) => ({
      ...r,
      campaignA: null,
      campaignB: null,
      peaceGive: [],
      peaceGet: [],
    }));
    return { ...save, relations, schema: 16 };
  },
  /** v16 → v17: automated workers (Phase 14d, ADR-0077). Nobody is automated yet. */
  16: (save) => {
    const units = ((save['units'] as Array<Record<string, unknown>> | undefined) ?? []).map((u) => ({ ...u, automated: false }));
    return { ...save, units, schema: 17 };
  },
};

export function migrate(save: RawSave, from: number): RawSave {
  if (from > CURRENT_SCHEMA) {
    throw new Error(
      `This save was written by a newer version of Epoch (save schema ${from}, this build reads ${CURRENT_SCHEMA}).`,
    );
  }

  let current = save;
  for (let v = from; v < CURRENT_SCHEMA; v++) {
    const step = MIGRATIONS[v];
    if (!step) {
      throw new Error(
        `This save is from an older, incompatible development build (save schema ${v}, this build reads ` +
          `${CURRENT_SCHEMA}) and there is no migration for it. Start a new game.`,
      );
    }
    current = step(current);
  }
  return current;
}
